import React from 'react';
import { useUser } from '../contexts/UserContext';
import { LogOut } from 'lucide-react';
import SignInWithTwitchButton from './SignInWithTwitchButton';
import { BrandLogo } from './BrandLogo';

function userInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase();
}

const Profile: React.FC = () => {
  const { user, loading, logout } = useUser();

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto" />
        <p className="mt-4 text-gray-600 dark:text-gray-300">Loading account...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <BrandLogo variant="brand" className="mx-auto justify-center mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Account</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            Sign in with Twitch to access your Scoutlytics account.
          </p>
          <SignInWithTwitchButton returnTo="/profile" />
        </div>
      </div>
    );
  }

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Account</h1>

        <div className="flex items-center space-x-4 mb-8">
          {user.profile_image_url ? (
            <img
              src={user.profile_image_url}
              alt=""
              className="h-20 w-20 rounded-full object-cover"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
              <span className="text-xl font-semibold text-purple-700 dark:text-purple-200">
                {userInitials(user.display_name || user.twitch_login)}
              </span>
            </div>
          )}
          <div>
            <p className="text-xl font-semibold text-gray-900 dark:text-white">
              {user.display_name}
            </p>
            <p className="text-gray-600 dark:text-gray-400">@{user.twitch_login}</p>
            {user.email && (
              <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">{user.email}</p>
            )}
          </div>
        </div>

        <dl className="space-y-4 mb-8">
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Report credits</dt>
            <dd className="text-lg text-gray-900 dark:text-white">{user.report_credits}</dd>
          </div>
          {memberSince && (
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Member since</dt>
              <dd className="text-lg text-gray-900 dark:text-white">{memberSince}</dd>
            </div>
          )}
        </dl>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Report history is coming soon.
        </p>

        <button
          type="button"
          onClick={() => logout()}
          className="inline-flex items-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign out</span>
        </button>
      </div>
    </div>
  );
};

export default Profile;
